import React, { useEffect } from 'react';
import { Card, Form, Button, Tabs, Input, message, Divider, Typography } from 'antd';
import { SaveOutlined, BankOutlined, TranslationOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title } = Typography;
const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

const DonateSettings: React.FC = () => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { data: config, isLoading } = useQuery({
        queryKey: ['adminDonateConfig'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/donate');
            return response.data.data;
        }
    });

    const updateMutation = useMutation({
        mutationFn: async (values: any) => {
            await apiClient.put('/admin/donate', values);
        },
        onSuccess: () => {
            message.success('Donation settings updated successfully');
            queryClient.invalidateQueries({ queryKey: ['adminDonateConfig'] });
        },
        onError: () => {
            message.error('Failed to update donation settings');
        }
    });

    useEffect(() => {
        if (config) {
            const translations = appLanguages.map(lang => {
                const trans = config.translations?.find((t: any) => t.languageCode === lang) || {};
                return {
                    languageCode: lang,
                    title: trans.title || '',
                    description: trans.description || '',
                    chairmanName: trans.chairmanName || ''
                };
            });

            form.setFieldsValue({
                bankName: config.bankName,
                iban: config.iban,
                accountHolder: config.accountHolder,
                registerNumber: config.registerNumber,
                localCourt: config.localCourt,
                translations
            });
        }
    }, [config, form]);

    const onFinish = (values: any) => {
        updateMutation.mutate(values);
    };

    if (isLoading) return <div>Loading...</div>;

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <Title level={2}>Donate Settings</Title>
                <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    size="large"
                    onClick={() => form.submit()}
                    loading={updateMutation.isPending}
                >
                    Save Changes
                </Button>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* Bank Details */}
                    <Card 
                        title={<><BankOutlined className="mr-2" /> Bank Information</>} 
                        className="lg:col-span-1 shadow-sm"
                    >
                        <Form.Item label="Bank Name" name="bankName" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="IBAN" name="iban" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="Account Holder" name="accountHolder" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                        <Divider />
                        <Form.Item label="Register Number" name="registerNumber">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Local Court" name="localCourt">
                            <Input />
                        </Form.Item>
                    </Card>

                    {/* Translations */}
                    <Card 
                        title={<><TranslationOutlined className="mr-2" /> Localized Content</>} 
                        className="lg:col-span-2 shadow-sm"
                    >
                        <Tabs
                            type="card"
                            items={appLanguages.map((lang, index) => ({
                                key: lang,
                                label: lang.toUpperCase(),
                                children: (
                                    <div className="p-4 border border-t-0 border-border rounded-b">
                                        <Form.Item hidden name={['translations', index, 'languageCode']}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item 
                                            label="Page Title" 
                                            name={['translations', index, 'title']}
                                            rules={[{ required: true }]}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item 
                                            label="Intro Description (HTML allowed)" 
                                            name={['translations', index, 'description']}
                                            rules={[{ required: true }]}
                                        >
                                            <Input.TextArea rows={6} />
                                        </Form.Item>
                                        <Form.Item 
                                            label="Chairman Name" 
                                            name={['translations', index, 'chairmanName']}
                                        >
                                            <Input />
                                        </Form.Item>
                                    </div>
                                )
                            }))}
                        />
                    </Card>
                </div>
            </Form>
        </div>
    );
};

export default DonateSettings;
